import { AdminOrderRow } from '../../../models/APIModels';

/**
 * Which leg of its order a row describes. An order with a return date is listed
 * twice — a `collection` row on its collection date and a `return` row on its
 * delivery date — and each row assigns the courier for its own leg.
 */
export type OrderLeg = 'collection' | 'delivery';

export function legOf(row: AdminOrderRow): OrderLeg {
  return row.type === 'return' || row.type === 'delivery' ? 'delivery' : 'collection';
}

/**
 * Sets one leg's courier on every row of an order. Both rows of an order show
 * both couriers, so writing only the row that was edited leaves its twin stale.
 */
export function withLegDriver(
  rows: AdminOrderRow[],
  orderId: number,
  leg: OrderLeg,
  driverId: number | null,
  driverName: string | null
): AdminOrderRow[] {
  return rows.map((r) => {
    if (r.id !== orderId) return r;
    return leg === 'collection'
      ? { ...r, collectionDriverId: driverId, collectionDriverName: driverName }
      : { ...r, deliveryDriverId: driverId, deliveryDriverName: driverName };
  });
}

/**
 * Applies a saved assignment to every row of its order. The assignment endpoint
 * describes the order as its collection leg — collection date, collection
 * address, `type: "collection"` — so only the assignment fields are taken from
 * it and each row keeps its own leg's type, date and address. Replacing whole
 * rows matched on `type` left the return row untouched.
 */
export function withAssignment(rows: AdminOrderRow[], saved: AdminOrderRow): AdminOrderRow[] {
  return rows.map((r) =>
    r.id !== saved.id
      ? r
      : {
          ...r,
          partnerId: saved.partnerId,
          partnerName: saved.partnerName,
          driverId: saved.driverId,
          driverName: saved.driverName,
          collectionDriverId: saved.collectionDriverId,
          collectionDriverName: saved.collectionDriverName,
          deliveryDriverId: saved.deliveryDriverId,
          deliveryDriverName: saved.deliveryDriverName,
          warehouseName: saved.warehouseName,
          collectionEta: saved.collectionEta,
          deliveryEta: saved.deliveryEta,
          collectionEtaStart: saved.collectionEtaStart,
          collectionEtaEnd: saved.collectionEtaEnd,
          deliveryEtaStart: saved.deliveryEtaStart,
          deliveryEtaEnd: saved.deliveryEtaEnd,
        }
  );
}
